import { createFileRoute } from "@tanstack/react-router"
import useEmblaCarousel from "embla-carousel-react"
import {
  ChevronLeft,
  ChevronRight,
  CircleDollarSign,
  FileSearch2Icon,
  FileText,
  Landmark,
  ShieldHalf,
} from "lucide-react"
import { useEffect, useState } from "react"
import { LogoHorizontal } from "@/components/logo-horizontal"
import { trpc } from "@/lib/trpc"

// Images
import hero from "../../../../public/images/hero-family.webp"
import listCheck from "../../../../public/images/icons/listCheck.svg"
import sprinto from "../../../../public/images/logo-sprinto.png"
// Partners
import invent from "../../../../public/images/partners/logo-invent.png"
import lawdepot from "../../../../public/images/partners/logo-lawdepot.png"
import notarypro from "../../../../public/images/partners/logo-notarypro.png"
import willful from "../../../../public/images/partners/logo-willful.png"
import s1 from "../../../../public/images/screenshots/fam-checklist.png"
import s2 from "../../../../public/images/screenshots/fam-collaboration.png"
import s3 from "../../../../public/images/screenshots/fam-data-policies.png"
import s4 from "../../../../public/images/screenshots/fam-estate-goals.png"
import s5 from "../../../../public/images/screenshots/fam-guided-form.png"
import s6 from "../../../../public/images/screenshots/fam-projections.png"
import s7 from "../../../../public/images/screenshots/pro-client-goals.png"
import s8 from "../../../../public/images/screenshots/pro-data-reconciliation.png"
import s9 from "../../../../public/images/screenshots/pro-document-extraction.png"
import s10 from "../../../../public/images/screenshots/pro-guided-intake.png"
import s11 from "../../../../public/images/screenshots/pro-insights.png"
import s12 from "../../../../public/images/screenshots/pro-visualization.png"
import BradHyde from "../../../../public/images/testimonials/BradHyde.jpeg"
import NicholasKelley from "../../../../public/images/testimonials/NicholasKelley.jpeg"
import TerryWright from "../../../../public/images/testimonials/TerryWright.png"

export const Route = createFileRoute("/_public/_homepage/")({
  component: Home,
})

type Audience = "professionals" | "families"

const region =
  typeof window !== "undefined" && Intl.DateTimeFormat().resolvedOptions().locale.includes("en-US")
    ? "us"
    : "ca"

const baseUrl = region === "us" ? "https://us.legacykeeper.app" : "https://ca.legacykeeper.app"

const audienceContent = {
  professionals: {
    intro:
      "Elevate your practice with AI-powered estate planning strategies that save time, personalize insights, and deliver deeper client value.",
    cards: [
      {
        image: s7,
        title: "Client Goals & Legacy Preferences",
        description:
          "Capture your clients' estate planning goals, legacy wishes, and family dynamics in a structured, shareable format.",
      },
      {
        image: s10,
        title: "Guided Data Entry Automation & Reconciliation",
        description:
          "Walk clients through a comprehensive estate planning questionnaire that extracts data from documents and covers every detail advisors need.",
      },
      {
        image: s9,
        title: "Estate Document Reviews",
        description:
          "Upload estate documents and deliver comprehensive reviews with actionable insights in minutes.",
      },
      {
        image: s8,
        title: "Comprehensive Estate Review",
        description:
          "Do a comprehensive review of client goals, relationships, tax, probate, insurance, trust, and corporate holdings in minutes instead of hours.",
      },
      {
        image: s11,
        title: "Personalized Client Insights & Recommendations",
        description:
          "Receive tailored insights and actionable recommendations based on each client's unique estate goals and circumstances.",
      },
      {
        image: s12,
        title: "Data Visualization & PowerPoint Download",
        description:
          "Generate beautiful visual reports of estate data — and download ready-to-present PowerPoint decks for client meetings.",
      },
    ],
  },
  families: {
    intro:
      "Take control of your family\u2019s legacy with intuitive tools that make estate planning simple, secure, and collaborative.",
    cards: [
      {
        image: s4,
        title: "Estate & Legacy Goals",
        description:
          "Define what matters most for your family's future and document your wishes for generations to come.",
      },
      {
        image: s5,
        title: "Automated Document Data Entry & Guided Estate Planning Form",
        description:
          "Step-by-step questions to build a complete picture of your estate — let our AI extract the details from your documents and enable you to reconcile where needed.",
      },
      {
        image: s1,
        title: "Estate Checklist",
        description:
          "Track your estate planning progress with a personalized checklist that shows you exactly what's done and what's missing.",
      },
      {
        image: s6,
        title: "Estate Assistant",
        description:
          "Know the details of your estate. Ask the assistant about report outputs, next steps, and for explanations.",
      },
      {
        image: s2,
        title: "Estate Collaboration",
        description:
          "Invite family members, attorneys, and advisors to collaborate on your estate plan in a secure shared workspace.",
      },
      {
        image: s3,
        title: "Family Focused User & Data Policies",
        description:
          "Your data stays yours. Built with family-first privacy policies — no selling data, no third-party sharing.",
      },
    ],
  },
}

const TESTIMONIALS = [
  {
    quote:
      "Estate planning is one of the most underserved areas in wealth management, and it's where clients feel the most vulnerable. Advisors who can guide families through it meaningfully — not just hand them off to an attorney — build the kind of trust that defines multi-generational relationships. That's why I'm working with the Legacy Keeper team to help shape what this should look like for our industry.",
    name: "Brad Hyde",
    role: "SVP Tax & Estate Planning",
    company: "Westgroup Financial Management",
    image: BradHyde,
  },
  {
    quote:
      "Clients want a single, secure place where their estate information lives that centralizes all of the PDF, filing cabinets, and email threads. For advisors, being the trusted holder of that information is a real differentiator, but only if the security and compliance side is rock solid. That's been a core focus of my conversations with the Legacy Keeper team.",
    name: "Terry Wright",
    role: "Wealth Advisor",
    company: "LT Wealth Management Partners",
    image: TerryWright,
  },
  {
    quote:
      "Estate and tax rules shift constantly — sunset provisions, state-level changes, evolving trust structures — and every change can ripple through hundreds of client plans overnight. Advisors shouldn't have to manually re-check each one. What drew me to Legacy Keeper is that they're building the kind of infrastructure that lets advisors keep up with that complexity at scale, without losing the personal touch that estate work demands.",
    name: "Nicholas Kelley, J.D",
    role: "Head of Product",
    company: "Worthy",
    image: NicholasKelley,
  },
]

const PRICING_PLANS = [
  {
    name: "Families",
    price: "Free",
    period: "",
    desc: "Perfect for individuals that want to start organizing and collaborating with their family on their estate plan.",
    features: [
      "Estate Readiness Score",
      "Guided estate planning intake",
      "Estate Vault with user focused data protection policies",
      "Document upload, data extraction and storage",
      "Asset Projections",
      "Estate checklist",
      "Estate collaboration",
      "Affiliate estate planning directory",
    ],
    popular: false,
    cta: "Get Started",
    href: "/sign-up",
  },
  {
    name: "Professional",
    price: "$100",
    period: "/month/seat",
    desc: "Built for Financial Advisors who want to guide clients through the estate planning journey while eliminating redundant data entry.",
    features: [
      "3 free client invitations for testing",
      "Client Management Dashboard",
      "Automated document classification, data extraction, centralization, and reconciliation",
      "Relationship trees, data visualization, and insights reports",
      "Report PowerPoint download",
      "Professional services directory",
      "Seamless partner document creation (no duplicate data entry)",
      "Team onboarding and training",
    ],
    popular: true,
    cta: "Get Started",
    href: "/sign-up",
  },
  {
    name: "Data Automation",
    price: "$250",
    period: "/month/seat",
    desc: "For advisory firms and fintechs that want to integrate our estate planning capabilities into their own offering via API or MCP.",
    features: [
      "Everything in Professional",
      "Automated workflows for enterprise account connection, client associations and client document classification",
      "Access your client's estate data via API",
      "Utilize AI functionality via MCP integration",
      "Technical integration support for $150/hr",
      "Premium support and training",
      "Technical referrals",
    ],
    popular: false,
    cta: "Contact Sales",
    href: "mailto:admin@legacykeeper.app",
  },
]

function PricingCard({ plan }: { plan: (typeof PRICING_PLANS)[number] }) {
  return (
    <div
      className={`rounded-3xl px-6 pb-6 lg:px-8 lg:pb-10 text-left relative flex flex-col ${
        plan.popular
          ? "border-2 pt-9 lg:pt-12 bg-[linear-gradient(135deg,#f7f7f7_20%,#e5fff9_100%)] border-legacyKeeperPrimary shadow-xl"
          : "border pt-7 lg:pt-12 bg-muted/40"
      }`}
    >
      {plan.popular ? (
        <div className="absolute w-full -top-5 left-0 right-0 flex justify-center">
          <span className="rounded-full bg-legacyKeeperPrimary px-8 py-2.5 text-white text-sm font-semibold tracking-wider">
            Most Popular
          </span>
        </div>
      ) : null}

      <span className="font-medium text-muted-foreground mb-1 block">{plan.name}</span>

      <h3 className="text-5xl dm-serif-display-regular mb-4">
        {plan.price}
        {plan.period ? <span className="text-lg text-muted-foreground">{plan.period}</span> : null}
      </h3>

      <p className="text-bodySmall text-muted-foreground">{plan.desc}</p>

      <hr className="my-8" />

      <ul className="space-y-2 text-sm text-bodySmall text-muted-foreground">
        {plan.features.map((feature) => (
          <li key={feature} className="flex items-start gap-2">
            <span className="size-5 text-legacyKeeperPrimary mt-[2px] flex-shrink-0">
              <img src={listCheck} alt="" />
            </span>
            <span>{feature}</span>
          </li>
        ))}
      </ul>

      <div className="mt-auto pt-10">
        <a
          href={plan.href}
          className={
            plan.popular
              ? "marketing-buttonPrimaryCta w-full block"
              : "marketing-buttonSecondaryCta w-full"
          }
        >
          {plan.cta}
        </a>
      </div>
    </div>
  )
}

export function AudienceSection() {
  const [audience, setAudience] = useState<Audience>("professionals")
  const [videoOpen, setVideoOpen] = useState(false)
  const content = audienceContent[audience]

  return (
    <>
      <div className="flex justify-center">
        <div className="inline-flex w-full md:w-auto rounded-lg md:rounded-full bg-muted p-1.5 border border-border gap-1">
          <button
            type="button"
            onClick={() => setAudience("professionals")}
            className={`flex-1 md:flex-none text-center md:text-left flex flex-col md:flex-row md:inline-flex items-center gap-2 rounded-lg md:rounded-full px-6 py-3.5 text-base transition ${
              audience === "professionals"
                ? "bg-white text-legacyKeeperPrimary-foreground border font-semibold"
                : "text-muted-foreground hover:text-foreground hover:bg-muted-foreground/10"
            }`}
          >
            <svg
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              className="size-5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15.75 6.75a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.5 20.25a7.5 7.5 0 0 1 15 0"
              />
            </svg>
            <span>For Financial Professionals</span>
          </button>

          <button
            type="button"
            onClick={() => setAudience("families")}
            className={`flex-1 md:flex-none text-center md:text-left flex flex-col md:flex-row md:inline-flex items-center gap-2 rounded-lg md:rounded-full px-6 py-3.5 text-base transition ${
              audience === "families"
                ? "bg-white text-legacyKeeperPrimary-foreground border font-semibold"
                : "text-muted-foreground hover:text-foreground hover:bg-muted-foreground/10"
            }`}
          >
            <svg
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="2"
              stroke="currentColor"
              className="size-5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M11.48 3.499a.562.562 0 0 1 1.04 0l2.125 5.111a.563.563 0 0 0 .475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 0 0-.182.557l1.285 5.385a.562.562 0 0 1-.84.61l-4.725-2.885a.562.562 0 0 0-.586 0L6.982 20.54a.562.562 0 0 1-.84-.61l1.285-5.386a.562.562 0 0 0-.182-.557l-4.204-3.602a.562.562 0 0 1 .321-.988l5.518-.442a.563.563 0 0 0 .475-.345L11.48 3.5Z"
              />
            </svg>

            <span>
              For <br className="md:hidden" />
              Families
            </span>
          </button>
        </div>
      </div>

      <p className="mx-auto mt-8 xl:max-w-xl text-body text-muted-foreground">
        {content.intro}
        {audience === "professionals" && (
          <>
            {" "}
            <button
              type="button"
              onClick={() => setVideoOpen(true)}
              className="text-legacyKeeperPrimary font-semibold hover:underline"
            >
              See how it works
            </button>
          </>
        )}
      </p>

      <div className="mt-12 grid gap-6 lg:gap-8 md:grid-cols-2 xl:grid-cols-3">
        {content.cards.map((card) => (
          <div
            key={card.title}
            className="overflow-hidden rounded-3xl border bg-muted/40 text-left"
          >
            <div className="h-64 bg-muted/60">
              <img src={card.image} alt="" className="h-full w-full object-cover" />
            </div>
            <div className="p-6 lg:p-8">
              <h3 className="dm-serif-display-regular text-xl leading-tight">{card.title}</h3>
              <p className="mt-3 text-bodySmall text-muted-foreground">{card.description}</p>
            </div>
          </div>
        ))}
      </div>

      {videoOpen && (
        // biome-ignore lint/a11y/noStaticElementInteractions: backdrop click-to-dismiss overlay
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={() => setVideoOpen(false)}
          onKeyDown={(e) => e.key === "Escape" && setVideoOpen(false)}
        >
          <div
            role="dialog"
            aria-label="See how it works video"
            className="relative w-full max-w-3xl mx-4"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.key === "Escape" && setVideoOpen(false)}
          >
            <button
              type="button"
              onClick={() => setVideoOpen(false)}
              className="absolute -top-10 right-0 text-white hover:text-white/80 text-sm font-medium"
            >
              Close
            </button>
            <div className="relative pt-[56.25%] rounded-2xl overflow-hidden bg-black">
              <iframe
                className="absolute inset-0 w-full h-full"
                src="https://www.youtube.com/embed/0rDUjpXKJSs?autoplay=1"
                title="See how Legacy Keeper works"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}
    </>
  )
}

type CalculatorInputs = {
  factFinders: number
  wills: number
  insurance: number
  trusts: number
  comprehensive: number
  rate: number
}

const CALCULATOR_FIELDS = [
  {
    key: "factFinders",
    label: "Customer Fact Finders",
    placeholder: "50",
    icon: FileSearch2Icon,
    hours: 2,
  },
  {
    key: "wills",
    label: "Will Document Reviews",
    placeholder: "40",
    icon: FileText,
    hours: 1.5,
  },
  {
    key: "insurance",
    label: "Life Insurance Reviews",
    placeholder: "30",
    icon: ShieldHalf,
    hours: 1,
  },
  {
    key: "trusts",
    label: "Trust Document Reviews",
    placeholder: "20",
    icon: Landmark,
    hours: 1.5,
  },
] as const

function ValueCalculator() {
  const [calcInputs, setCalcInputs] = useState<CalculatorInputs>({
    factFinders: 0,
    wills: 0,
    insurance: 0,
    trusts: 0,
    comprehensive: 0,
    rate: 0,
  })

  const calcHours =
    calcInputs.factFinders * 2 +
    calcInputs.wills * 1.5 +
    calcInputs.insurance * 1 +
    calcInputs.trusts * 1.5 +
    calcInputs.comprehensive * 3

  const calcValue = calcHours * calcInputs.rate
  const calcRoi = calcValue > 0 ? Math.round((calcValue / 1200) * 10) / 10 : 0

  const updateCalcInput = (field: keyof CalculatorInputs, value: string) => {
    setCalcInputs((prev) => ({
      ...prev,
      [field]: Number(value) || 0,
    }))
  }

  return (
    <>
      <div className="mb-14 text-center">
        <div className="mb-3">
          <span className="text-xs uppercase tracking-widest text-legacyKeeperPrimary font-medium">
            Savings
          </span>
        </div>
        <h2 className="text-4xl xl:text-5xl md:leading-tight dm-serif-display-regular mb-4">
          Calculate Your ROI in 60 Seconds
        </h2>
        <p className="text-muted-foreground text-bodyLarge">
          Answer a few quick questions to see your potential savings.
        </p>
      </div>

      <div className="grid gap-8 lg:grid-cols-2">
        {/* Left card */}
        <div className="rounded-3xl bg-white p-6 md:p-10 border">
          <h3 className="text-2xl mb-4 md:mb-8 dm-serif-display-regular">Your Practice Details</h3>

          <div className="rounded-xl border-2 border-legacyKeeperPrimary/40 bg-legacyKeeperPrimary/5 p-5 mb-8">
            <div className="flex items-center gap-5">
              <div className="flex size-14 shrink-0 items-center justify-center rounded-xl bg-legacyKeeperPrimary text-2xl text-white">
                <CircleDollarSign />
              </div>

              <div className="w-full text-left">
                <label htmlFor="calc-rate" className="block font-semibold mb-3">
                  What's your hourly rate?
                </label>

                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground font-semibold">
                    $
                  </span>
                  <input
                    id="calc-rate"
                    type="number"
                    min="0"
                    placeholder="250"
                    onChange={(e) => updateCalcInput("rate", e.target.value)}
                    className="w-full rounded-xl border border-legacyKeeperPrimary/60 bg-white py-3 pl-10 pr-4 text-lg outline-none focus:ring-2 focus:ring-legacyKeeperPrimary/20"
                  />
                </div>
              </div>
            </div>
          </div>

          <p className="mb-5 text-bodyLarge">Annual review volumes:</p>

          <div className="space-y-4">
            {CALCULATOR_FIELDS.map((field) => (
              <CalculatorVolumeField
                key={field.key}
                id={`calc-${field.key}`}
                icon={field.icon}
                label={field.label}
                placeholder={field.placeholder}
                onChange={(value) => updateCalcInput(field.key, value)}
              />
            ))}
          </div>
        </div>

        {/* Right card */}
        <div className="rounded-3xl bg-legacyKeeperPrimary p-6 md:p-10 text-white shadow-xl">
          <h3 className="text-2xl mb-4 md:mb-8 dm-serif-display-regular">
            Your Potential Annual Savings
          </h3>

          <div className="space-y-6">
            <SavingsResult
              label="Time Saved"
              value={calcHours.toLocaleString()}
              suffix="hours per year"
            />
            <SavingsResult
              label="Money Saved"
              value={`$${calcValue.toLocaleString()}`}
              suffix="in annual value"
            />
            <SavingsResult
              label="Return on Investment"
              value={`${calcRoi.toLocaleString()}x`}
              suffix="annual return"
            />
          </div>

          <a
            href="/sign-up"
            className="mt-8 flex w-full items-center justify-center rounded-xl bg-white px-6 py-4 text-center font-semibold text-legacyKeeperPrimary-foreground transition hover:bg-white/90"
          >
            Claim Your Savings — Start Free Trial
          </a>
        </div>
      </div>
    </>
  )
}

function CalculatorVolumeField({
  id,
  icon: Icon,
  label,
  placeholder,
  onChange,
}: {
  id: string
  icon: React.ComponentType<{ className?: string }>
  label: string
  placeholder: string
  onChange: (value: string) => void
}) {
  return (
    <div className="flex flex-col gap-4 rounded-2xl border-2 border-muted bg-white p-4 sm:flex-row sm:items-center sm:gap-5">
      <div className="flex items-center gap-4 sm:flex-1">
        <div className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-legacyKeeperPrimary/10 text-legacyKeeperPrimary">
          <Icon className="size-6" />
        </div>

        <label htmlFor={id} className="font-semibold leading-tight">
          {label}
        </label>
      </div>

      <div className="flex items-center gap-2 sm:shrink-0">
        <input
          id={id}
          type="number"
          min="0"
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          className="w-full sm:w-28 rounded-xl border bg-white px-4 py-3 text-center text-lg outline-none focus:ring-2 focus:ring-legacyKeeperPrimary/20"
        />

        <span className="text-bodySmall whitespace-nowrap">/year</span>
      </div>
    </div>
  )
}

function SavingsResult({ label, value, suffix }: { label: string; value: string; suffix: string }) {
  return (
    <div className="rounded-2xl bg-white/10 p-6">
      <div className="text-body font-medium text-white/90">{label}</div>
      <div className="mt-1 text-5xl md:text-6xl font-bold leading-none">{value}</div>
      <div className="mt-1 text-body text-white/90">{suffix}</div>
    </div>
  )
}

function TestimonialsCarousel() {
  const testimonialCount = TESTIMONIALS.length
  const isTwoTestimonials = testimonialCount === 2
  const shouldShowControls = testimonialCount >= 3

  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    loop: shouldShowControls,
    slidesToScroll: 1,
  })

  const scrollPrev = () => emblaApi?.scrollPrev()
  const scrollNext = () => emblaApi?.scrollNext()

  const TESTIMONIAL_GRADIENTS = [
    "bg-[linear-gradient(135deg,#ffffff_50%,#e5fff9_100%)]",
    "bg-[linear-gradient(-45deg,#ffffff_50%,#e5fff9_100%)]",
    "bg-[linear-gradient(190deg,#ffffff_50%,#e5fff9_100%)]",
  ]

  let viewportClassName = ""
  if (shouldShowControls) {
    viewportClassName = "overflow-hidden"
  }

  let trackClassName = ""
  if (isTwoTestimonials) {
    trackClassName = "grid gap-6 md:grid-cols-2"
  }

  if (shouldShowControls) {
    trackClassName = "-ml-6 flex items-stretch"
  }

  return (
    <div className="mx-auto container px-6 xl:px-40 mt-16">
      <div className="min-w-0">
        <div className={viewportClassName} ref={emblaRef}>
          <div className={trackClassName}>
            {TESTIMONIALS.map((testimonial, index) => {
              const gradientClass = TESTIMONIAL_GRADIENTS[index % TESTIMONIAL_GRADIENTS.length]

              let articleClassName = "w-full"

              if (shouldShowControls) {
                articleClassName = "min-w-0 flex-[0_0_90%] pl-8 md:flex-[0_0_50%] self-stretch"
              }

              return (
                <article key={`${testimonial.name}-${index}`} className={articleClassName}>
                  <div
                    className={`flex h-full min-h-[380px] flex-col rounded-3xl ${gradientClass} p-8 md:p-10 border`}
                  >
                    <div className="mb-6">
                      <div className="size-24 overflow-hidden rounded-full bg-muted border">
                        {testimonial.image ? (
                          <img
                            src={testimonial.image}
                            alt={testimonial.name}
                            className="size-full object-cover"
                          />
                        ) : (
                          <div className="flex size-full items-center justify-center text-sm font-semibold text-muted-foreground">
                            {testimonial.name
                              .split(" ")
                              .map((part) => part[0])
                              .join("")
                              .slice(0, 2)}
                          </div>
                        )}
                      </div>
                    </div>

                    <p className="text-body leading-relaxed">"{testimonial.quote}"</p>

                    <div className="mt-auto pt-14">
                      <h3 className="text-2xl dm-serif-display-regular">{testimonial.name}</h3>
                      <p className="mt-1 text-xs font-light text-muted-foreground">
                        {testimonial.role}
                        {testimonial.company ? `, ${testimonial.company}` : ""}
                      </p>
                    </div>
                  </div>
                </article>
              )
            })}
          </div>
        </div>
      </div>

      {shouldShowControls && (
        <div className="mt-8 flex justify-center gap-2">
          <button
            type="button"
            onClick={scrollPrev}
            aria-label="Previous testimonials"
            className="flex size-16 items-center justify-center rounded-full border bg-white hover:bg-muted hover:border-legacyKeeperPrimary text-muted-foreground transition-all ease-in-out duration-200"
          >
            <ChevronLeft />
          </button>

          <button
            type="button"
            onClick={scrollNext}
            aria-label="Next testimonials"
            className="flex size-16 items-center justify-center rounded-full border bg-white hover:bg-muted hover:border-legacyKeeperPrimary text-muted-foreground transition-all ease-in-out duration-200"
          >
            <ChevronRight />
          </button>
        </div>
      )}
    </div>
  )
}

function MobileNavArrow() {
  return (
    <svg
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      className="size-5 lg:hidden"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
    </svg>
  )
}

function Home() {
  const [scrolled, setScrolled] = useState(false)
  const [hidden, setHidden] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [openFaq, setOpenFaq] = useState<number | null>(null)
  const [contactFormSent, setContactFormSent] = useState(false)

  const contactFormMutation = trpc.contactForm.send.useMutation({
    onSuccess: () => {
      setContactFormSent(true)
    },
  })

  type FAQ = {
    question: string
    answer: string[]
  }

  const faqs: FAQ[] = [
    {
      question: "What is Legacy Keeper?",
      answer: [
        "Legacy Keeper is an AI-first estate planning platform designed for financial advisors and families. It streamlines the entire estate planning workflow \u2014 from client intake and document data extraction to estate data reconciliation and personalized insights.",
      ],
    },
    {
      question: "What is AI-first estate planning?",
      answer: [
        "An AI-first company is an organization that embeds artificial intelligence as its core infrastructure, strategy, and operating model rather than treating it as an add-on tool like traditional SaaS software. AI-first estate planning leverages artificial intelligence to streamline and enhance the estate planning process.",
      ],
    },
    {
      question: "How does document data extraction work?",
      answer: [
        "Simply upload estate documents such as wills, trusts, insurance policies, or account statements. Our AI reads and extracts the key data points from each document, organizing them into a structured format that\u2019s ready for review and reconciliation.",
      ],
    },
    {
      question: "Is Legacy Keeper secure?",
      answer: [
        "Absolutely. We use bank-level encryption and rest service architecture for data in transit. All data and documents are stored on AWS cloud infrastructure. We never sell your data to third parties.",
      ],
    },
    {
      question: "Can I use Legacy Keeper for my own family\u2019s estate planning?",
      answer: [
        "Yes! Legacy Keeper offers a family-focused plan with guided estate planning forms, an estate checklist, asset projections, and collaboration tools so you can invite family members and advisors to work together on your plan.",
      ],
    },
    {
      question: "How is Legacy Keeper different from other estate planning tools?",
      answer: [
        "Unlike traditional estate planning software that focuses mainly on document drafting, Legacy Keeper generates personalized insights and visual reports \u2014 saving advisors hours of manual work. We have the most protective data policies for families, and our platform is designed to facilitate collaboration between families and their advisors in one secure workspace.",
      ],
    },
    {
      question: "What types of documents can I upload?",
      answer: [
        "Legacy Keeper supports wills, trusts, power of attorney documents, insurance policies, bank and brokerage statements, real estate deeds, beneficiary designations, and more.",
      ],
    },
    {
      question: "Is there a free plan available?",
      answer: [
        "Yes! Our Free plan is completely free and includes guided estate planning intake, basic document uploads, an estate checklist, and email support. Professional users pay to generate insights and reports for their clients.",
      ],
    },
    {
      question: "Can I download reports for client presentations?",
      answer: [
        "Yes. Professional plans include data visualization and the ability to download fully formatted PowerPoint presentations \u2014 ready for your next client meeting.",
      ],
    },
  ]

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? "hidden" : ""

    return () => {
      document.body.style.overflow = ""
    }
  }, [mobileMenuOpen])

  useEffect(() => {
    let lastScrollY = window.scrollY

    const handleScroll = () => {
      const currentScrollY = window.scrollY
      const pastBlurPoint = currentScrollY > 50
      const pastHidePoint = currentScrollY > window.innerHeight / 2
      const scrollingDown = currentScrollY > lastScrollY

      setScrolled(pastBlurPoint)
      setHidden(pastHidePoint && scrollingDown)

      lastScrollY = currentScrollY
    }

    window.addEventListener("scroll", handleScroll, { passive: true })

    return () => {
      window.removeEventListener("scroll", handleScroll)
    }
  }, [])

  return (
    <main
      id="landingPageWrapper"
      className="bg-[linear-gradient(135deg,#f5f1ec_0%,#9affe7_100%)] pt-20 lg:pt-28"
    >
      {/* NAV */}
      <div
        className={`fixed top-0 z-50 w-full transition-all duration-300 ${
          scrolled || mobileMenuOpen ? "bg-white/70 backdrop-blur-sm" : "bg-transparent"
        } ${hidden && !mobileMenuOpen ? "-translate-y-full" : "translate-y-0"}`}
      >
        <div className="mx-auto container px-6 xl:px-40">
          <header className="flex items-center justify-between py-5">
            <div className="relative z-50">
              <LogoHorizontal />
            </div>

            <nav
              className={`fixed inset-0 z-40 flex h-screen w-full flex-col items-start text-left lg:gap-6 bg-white text-nav text-muted-foreground transition-all duration-300 lg:static lg:z-auto lg:h-auto lg:w-auto lg:flex-row lg:items-center lg:text-center lg:justify-start lg:bg-transparent px-6 pt-28 pb-6 lg:px-0 lg:pt-0 lg:pb-0 xl:gap-7 ${
                mobileMenuOpen
                  ? "translate-y-0 opacity-100 pointer-events-auto"
                  : "-translate-y-full opacity-0 pointer-events-none lg:translate-y-0 lg:opacity-100 lg:pointer-events-auto"
              }`}
            >
              {/* biome-ignore lint/a11y/useValidAnchor: Same-page hash link for section navigation */}
              <a
                href="#why"
                onClick={() => setMobileMenuOpen(false)}
                className="flex w-full items-center justify-between border-b border-border/75 py-4 lg:py-0 hover:text-foreground lg:w-auto lg:border-0 lg:pb-0"
              >
                <span>Who is this for</span>
                <MobileNavArrow />
              </a>
              {/* biome-ignore lint/a11y/useValidAnchor: Same-page hash link for section navigation */}
              <a
                href="#pricing"
                onClick={() => setMobileMenuOpen(false)}
                className="flex w-full items-center justify-between border-b border-border/75 py-4 lg:py-0 hover:text-foreground lg:w-auto lg:border-0 lg:pb-0"
              >
                <span>Pricing</span>
                <MobileNavArrow />
              </a>
              {/* biome-ignore lint/a11y/useValidAnchor: Same-page hash link for section navigation */}
              <a
                href="#roi"
                onClick={() => setMobileMenuOpen(false)}
                className="flex w-full items-center justify-between border-b border-border/75 py-4 lg:py-0 hover:text-foreground lg:w-auto lg:border-0 lg:pb-0"
              >
                <span>ROI</span>
                <MobileNavArrow />
              </a>
              {/* biome-ignore lint/a11y/useValidAnchor: Same-page hash link for section navigation */}
              <a
                href="#contact"
                onClick={() => setMobileMenuOpen(false)}
                className="flex w-full items-center justify-between border-b border-border/75 py-4 lg:py-0 hover:text-foreground lg:w-auto lg:border-0 lg:pb-0"
              >
                <span>Contact</span>
                <MobileNavArrow />
              </a>
              {/* biome-ignore lint/a11y/useValidAnchor: Same-page hash link for section navigation */}
              <a
                href="#faq"
                onClick={() => setMobileMenuOpen(false)}
                className="flex w-full items-center justify-between border-b border-border/75 py-4 lg:py-0 hover:text-foreground lg:w-auto lg:border-0 lg:pb-0"
              >
                <span>FAQ</span>
                <MobileNavArrow />
              </a>
              <a
                href="https://legacykeeper.app/blog/"
                onClick={() => setMobileMenuOpen(false)}
                className="flex w-full items-center justify-between border-b border-border/75 py-4 lg:py-0 hover:text-foreground lg:w-auto lg:border-0 lg:pb-0"
              >
                <span>Blog</span>
                <MobileNavArrow />
              </a>
              <a
                href="https://podcast.legacykeeper.app"
                onClick={() => setMobileMenuOpen(false)}
                className="flex w-full items-center justify-between border-b border-border/75 py-4 lg:py-0 hover:text-foreground lg:w-auto lg:border-0 lg:pb-0"
              >
                <span>Podcast</span>
                <MobileNavArrow />
              </a>
              <div className="mt-auto flex w-full flex-col gap-4 pb-8 lg:hidden">
                <a href="/sign-up" className="marketing-buttonPrimary w-full">
                  Create Account
                </a>
                <a href="/sign-in" className="marketing-buttonSecondary w-full">
                  Log in
                </a>
              </div>
            </nav>

            <div className="flex items-center gap-3 md:gap-4">
              <a href="/sign-in" className="hidden lg:inline marketing-buttonSecondary">
                <span className="hidden md:block">Log in</span>
                <svg
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  className="size-6 md:hidden"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z"
                  />
                </svg>
              </a>
              <a href="/sign-up" className="hidden lg:block marketing-buttonPrimary">
                Create Account
              </a>
              <a
                href="/sign-in"
                className="hidden sm:block lg:hidden rounded-full border border-muted-foreground text-muted-foreground font-semibold px-4 py-2 text-bodySmall"
              >
                Log in
              </a>
              <button
                type="button"
                aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
                aria-expanded={mobileMenuOpen}
                onClick={() => setMobileMenuOpen((open) => !open)}
                className="relative z-[60] flex h-10 w-10 flex-col items-center justify-center gap-1.5 lg:hidden"
              >
                <span
                  className={`block h-[2.5px] w-7 rounded-full bg-foreground transition-all duration-300 ${
                    mobileMenuOpen ? "translate-y-2 rotate-45" : ""
                  }`}
                />
                <span
                  className={`block h-[2.5px] w-7 rounded-full bg-foreground transition-all duration-300 ${
                    mobileMenuOpen ? "opacity-0" : ""
                  }`}
                />
                <span
                  className={`block h-[2.5px] w-7 rounded-full bg-foreground transition-all duration-300 ${
                    mobileMenuOpen ? "-translate-y-2 -rotate-45" : ""
                  }`}
                />
              </button>
            </div>
          </header>
        </div>
      </div>

      {/* HERO */}
      <section className="pt-16 pb-24">
        <div className="mx-auto grid container px-6 xl:px-40 gap-10 lg:grid-cols-12">
          <div className="flex flex-col lg:col-span-7 justify-center gap-6 md:gap-8">
            <div>
              <span className="md:mb-4 text-[10px] md:text-xs uppercase tracking-wide md:tracking-widest text-legacyKeeperPrimary-foreground bg-legacyKeeperPrimary/10 border border-legacyKeeperPrimary/20 px-3 py-2 md:px-5 md:py-3 rounded-full font-medium">
                Estate planning for financial advisors
              </span>
            </div>
            <div className="flex gap-4 flex-col">
              <h1 className="text-5xl lg:text-6xl font-semibold md:leading-tight dm-serif-display-regular">
                Help Protect What Matters Most To Your Clients &mdash;
                <span className="text-legacyKeeperPrimary-foreground">
                  {" "}
                  Their Family&rsquo;s Legacy
                </span>
              </h1>
              <p className="text-bodyLarge text-muted-foreground">
                Legacy Keeper streamlines the entire estate planning workflow &mdash; from client
                intake and document data extraction to data reconciliation and personalized
                insights. Empower your clients with the most comprehensive client estate data
                privacy policies.
              </p>
              <div className="mt-6 flex flex-col md:flex-row gap-4">
                <a
                  href="https://app.apollo.io/#/meet/jon_mason_ca6"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="marketing-buttonPrimaryCta"
                >
                  Book a Demo
                </a>
                <a href="#why" className="marketing-buttonSecondaryCta">
                  See How It Works
                </a>
              </div>
            </div>
            <p className="text-muted-foreground text-bodySmall flex items-center gap-2">
              <span>
                <svg
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="size-4.5 text-legacyKeeperPrimary"
                >
                  <path
                    fillRule="evenodd"
                    d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.006 5.404.434c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.434 2.082-5.005Z"
                    clipRule="evenodd"
                  />
                </svg>
              </span>
              Trusted by fiduciary advisors
            </p>
          </div>

          {/* HERO CARD */}
          <div className="flex items-center justify-center lg:col-span-5">
            <img
              src={hero}
              alt="Multigenerational family sharing a meal together, smiling and enjoying each other's company"
              className="lp-hero-image w-full rounded-3xl shadow-xl"
              width="560"
              height="420"
            />
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="why" className="bg-white py-20 xl:py-24">
        <div className="mx-auto container px-6 xl:px-40 text-center">
          <div className="mb-3">
            <span className="text-xs uppercase tracking-widest text-legacyKeeperPrimary font-medium">
              Who is this for
            </span>
          </div>
          <h2 className="text-4xl xl:text-5xl md:leading-tight font-semibold dm-serif-display-regular">
            Estate Planning For Financial Advisors <br className="hidden lg:inline-block" />& The
            Families They Serve
          </h2>
          <div className="mt-4 mb-9 lg:px-40 xl:px-72">
            <p className="text-muted-foreground text-bodyLarge">
              Whether you&rsquo;re a financial advisor guiding your client through an estate
              strategy or a family planning for the future, Legacy Keeper has you covered.
            </p>
          </div>

          <AudienceSection />
          <div className="mt-10 lg:mt-14">
            <a
              href="https://app.apollo.io/#/meet/jon_mason_ca6"
              target="_blank"
              rel="noopener noreferrer"
              className="marketing-buttonPrimaryCta"
            >
              Book a Demo
            </a>
          </div>
        </div>
      </section>

      <section className="bg-muted py-20 xl:py-24">
        <div className="mx-auto container px-6 xl:px-40 text-center mb-6">
          <h2 className="text-4xl xl:text-5xl md:leading-tight font-semibold dm-serif-display-regular">
            Built with financial advisors
          </h2>
          <div className="mt-4 mb-9 lg:px-40 xl:px-72">
            <p className="text-muted-foreground text-bodyLarge">
              Whether you're a financial advisor managing client estates or a family planning for
              the future, Legacy Keeper has you covered.
            </p>
          </div>
        </div>
        <div className="mx-auto container px-6 xl:px-40 border-b border-muted-foreground/20">
          <div className="px-4 lg:px-0 pt-6 pb-6 md:pt-10 grid grid-cols-2 sm:grid-cols-4 text-center gap-5">
            <div>
              <div className="text-5xl text-legacyKeeperPrimary font-semibold tracking-tighter mb-1.5">
                10+
              </div>
              <div className="text-xs md:text-sm opacity-80">Pre-built Reports</div>
            </div>
            <div>
              <div className="text-5xl text-legacyKeeperPrimary font-semibold tracking-tighter mb-1.5">
                100%
              </div>
              <div className="text-xs md:text-sm opacity-80">Satisfaction Guarantee</div>
            </div>
            <div>
              <div className="text-5xl text-legacyKeeperPrimary font-semibold tracking-tighter mb-1.5">
                &infin;
              </div>
              <div className="text-xs md:text-sm opacity-80">Client Reports</div>
            </div>
            <div>
              <div className="text-5xl text-legacyKeeperPrimary font-semibold tracking-tighter mb-1.5">
                2+hrs
              </div>
              <div className="text-xs md:text-sm opacity-80">Saved Per Client</div>
            </div>
          </div>
        </div>

        <TestimonialsCarousel />
        <div className="container mx-auto px-6 xl:px-40 mt-16">
          <h3 className="text-center text-sm uppercase tracking-widest mb-8 text-muted-foreground">
            Trusted Providers
          </h3>
          <div className="flex flex-row flex-wrap justify-center gap-6 lg:gap-12 items-center">
            <img className="h-[50px] md:h-[80px]" src={invent} alt="Invent logo" />
            <img className="h-[50px] md:h-[80px]" src={notarypro} alt="NotaryPro logo" />
            <img className="h-[50px] md:h-[80px]" src={willful} alt="Willful logo" />
            <img className="h-[50px] md:h-[80px]" src={lawdepot} alt="LawDepot logo" />
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="bg-white py-20 xl:py-24">
        <div className="mx-auto container px-6 xl:px-40 text-center">
          <div className="mb-3">
            <span className="text-xs uppercase tracking-widest text-legacyKeeperPrimary font-medium">
              Pricing
            </span>
          </div>
          <h2 className="text-4xl xl:text-5xl md:leading-tight dm-serif-display-regular mb-4">
            Simple, Transparent Pricing
          </h2>
          <p className="text-muted-foreground text-bodyLarge">
            Free trial. No hidden fees, cancel anytime.
          </p>
          <div className="mt-12 lg:mt-16 grid gap-6 lg:grid-cols-3">
            {PRICING_PLANS.map((plan) => (
              <PricingCard key={plan.name} plan={plan} />
            ))}
          </div>
        </div>
      </section>

      {/* ROI */}
      <section id="roi" className="bg-muted py-20 xl:py-24">
        <div className="mx-auto container px-6 xl:px-40 text-center">
          <ValueCalculator />
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="bg-white py-20 xl:py-24">
        <div className="mx-auto container px-6 xl:px-40 grid gap-10 lg:grid-cols-2">
          <div className="text-left flex flex-col lg:pr-10 xl:pr-16">
            <div className="mb-3">
              <span className="text-xs uppercase tracking-widest text-legacyKeeperPrimary font-medium">
                Contact us
              </span>
            </div>
            <h2 className="text-4xl xl:text-5xl md:leading-tight font-semibold dm-serif-display-regular">
              Let's talk about your estate planning needs
            </h2>
            <div className="mt-4 mb-10">
              <p className="text-muted-foreground text-bodyLarge">
                Whether you're a financial advisor looking to scale your practice or have questions
                about our platform, we'd love to hear from you.
              </p>
            </div>
            <div className="flex flex-col gap-4">
              <p className="flex items-center gap-4">
                <span className="text-legacyKeeperPrimary">
                  <svg
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="size-6"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75"
                    />
                  </svg>
                </span>
                <a
                  href="mailto:admin@legacykeeper.app"
                  className="text-muted-foreground text-bodyLarge hover:underline"
                >
                  admin@legacykeeper.app
                </a>
              </p>
              <p className="flex items-center gap-4">
                <span className="text-legacyKeeperPrimary">
                  <svg
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="size-6"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 20.25c4.97 0 9-3.694 9-8.25s-4.03-8.25-9-8.25S3 7.444 3 12c0 2.104.859 4.023 2.273 5.48.432.447.74 1.04.586 1.641a4.483 4.483 0 0 1-.923 1.785A5.969 5.969 0 0 0 6 21c1.282 0 2.47-.402 3.445-1.087.81.22 1.668.337 2.555.337Z"
                    />
                  </svg>
                </span>
                <a
                  href="mailto:admin@legacykeeper.app"
                  className="text-muted-foreground text-bodyLarge hover:underline"
                >
                  Typical response within 24 hours
                </a>
              </p>
            </div>
            <a
              href="https://app.apollo.io/#/meet/jon_mason_ca6"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-8 inline-flex items-center justify-center rounded-full bg-legacyKeeperPrimary px-8 py-3 text-sm font-semibold text-white shadow-md hover:opacity-90 transition-opacity w-fit"
            >
              Book a Demo
            </a>
          </div>

          {contactFormSent ? (
            <div className="bg-white p-6 md:p-10 rounded-3xl border">
              <h3 className="text-2xl dm-serif-display-regular mb-3">Thank you</h3>
              <p className="text-muted-foreground text-bodyLarge">
                Your message has been sent. We’ll get back to you as soon as possible.
              </p>
            </div>
          ) : (
            <form
              className="bg-white p-6 md:p-10 rounded-3xl border flex flex-col gap-4"
              onSubmit={(e) => {
                e.preventDefault()

                const formData = new FormData(e.currentTarget)

                contactFormMutation.mutate({
                  name: String(formData.get("name") || ""),
                  email: String(formData.get("email") || ""),
                  company: String(formData.get("company") || ""),
                  message: String(formData.get("message") || ""),
                  website: String(formData.get("website") || ""),
                })
              }}
            >
              <div className="hidden" aria-hidden="true">
                <label htmlFor="website">Website</label>
                <input id="website" name="website" tabIndex={-1} autoComplete="off" />
              </div>
              <div className="flex flex-col gap-4 md:flex-row">
                <div className="w-full">
                  <label htmlFor="name" className="block text-sm font-medium mb-2">
                    Name
                  </label>
                  <input
                    id="name"
                    name="name"
                    required
                    placeholder="John Smith"
                    className="w-full border p-3 rounded-lg bg-muted/50"
                  />
                </div>

                <div className="w-full">
                  <label htmlFor="email" className="block text-sm font-medium mb-2">
                    Email
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    placeholder="john@example.com"
                    className="w-full border p-3 rounded-lg bg-muted/50"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="company" className="block text-sm font-medium mb-2">
                  Company / Firm{" "}
                  <span className="font-light text-muted-foreground">(Optional)</span>
                </label>
                <input
                  id="company"
                  name="company"
                  placeholder="Your company name"
                  className="w-full border p-3 rounded-lg bg-muted/50"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium mb-2">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  placeholder="Tell us how we can help..."
                  className="w-full border p-3 rounded-lg bg-muted/50"
                  rows={4}
                />
              </div>

              {contactFormMutation.isError ? (
                <p className="text-sm text-destructive">
                  Sorry, something went wrong. Please try again or email admin@legacykeeper.app.
                </p>
              ) : null}

              <button
                type="submit"
                disabled={contactFormMutation.isPending}
                className="marketing-buttonPrimaryCta w-full block disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {contactFormMutation.isPending ? "Sending..." : "Send Message"}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="bg-muted py-20 xl:py-24">
        <div className="mx-auto container px-6 xl:px-40 text-center">
          <div className="mb-3">
            <span className="text-xs uppercase tracking-widest text-legacyKeeperPrimary font-medium">
              FAQs
            </span>
          </div>
          <h2 className="text-4xl xl:text-5xl md:leading-tight dm-serif-display-regular mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-muted-foreground text-bodyLarge">
            Everything you need to know about Legacy Keeper.{" "}
            <br className="hidden lg:inline-block" />
            Can't find what you're looking for?{" "}
            <a
              href="#contact"
              className="text-legacyKeeperPrimary-foreground underline underline-offset-4 hover:underline-offset-8 transition-all duration-200 ease-in-out"
            >
              Contact us
            </a>
          </p>

          <div className="flex w-full justify-center">
            <div className="mt-10 md:w-3/5 space-y-3">
              {faqs.map((faq, index) => {
                const isOpen = openFaq === index

                return (
                  <div
                    key={faq.question}
                    className="rounded-xl border border-muted-foreground/25 bg-muted overflow-clip"
                  >
                    <button
                      type="button"
                      onClick={() => setOpenFaq(isOpen ? null : index)}
                      className="flex w-full items-center justify-between text-left text-muted-foreground p-4 cursor-pointer hover:bg-muted-foreground/10 transition-all ease-in-out duration-200"
                      aria-expanded={isOpen}
                    >
                      <span className="font-medium text-black/80">{faq.question}</span>

                      <svg
                        aria-hidden="true"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        className={`size-5 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
                        fill="none"
                        strokeWidth="1.5"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="m19.5 8.25-7.5 7.5-7.5-7.5"
                        />
                      </svg>
                    </button>

                    <div
                      className={`grid transition-all duration-300 ease-in-out ${
                        isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                      }`}
                    >
                      <div className="overflow-hidden px-4">
                        <div className="mt-2 mb-5 text-left text-muted-foreground">
                          {faq.answer.map((paragraph) => (
                            <p key={paragraph} className="mb-2">
                              {paragraph}
                            </p>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-black/95 text-white pt-16 pb-7 text-sm">
        <div className="mx-auto container px-6 xl:px-40 grid grid-cols-1 md:grid-cols-12 gap-6">
          <div className="md:pr-10 col-span-1 md:col-span-5 lg:pr-40">
            <div>
              <LogoHorizontal />
            </div>
            <p className="mt-4 text-muted-foreground text-bodySmall">
              Estate Planning For Financial Advisors & The Families They Serve
            </p>
            <div className="mt-6">
              <h3 className="dm-serif-display-regular text-md">Follow us</h3>
              <div className="mt-3 flex gap-3">
                <a
                  href="https://www.youtube.com/@LegacyKeeperApp"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Legacy Keeper on YouTube"
                  className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-white/15 text-white/70 hover:border-primary hover:text-primary transition-all"
                >
                  <span className="sr-only">YouTube</span>
                  <svg
                    width="19"
                    height="19"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M2.5 17a24.12 24.12 0 0 1 0-10 2 2 0 0 1 1.4-1.4 49.56 49.56 0 0 1 16.2 0A2 2 0 0 1 21.5 7a24.12 24.12 0 0 1 0 10 2 2 0 0 1-1.4 1.4 49.55 49.55 0 0 1-16.2 0A2 2 0 0 1 2.5 17" />
                    <path d="m10 15 5-3-5-3z" />
                  </svg>
                </a>
                <a
                  href="https://www.linkedin.com/company/legacy-keeper/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Legacy Keeper on LinkedIn"
                  className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-white/15 text-white/70 hover:border-primary hover:text-primary transition-all"
                >
                  <span className="sr-only">LinkedIn</span>
                  <svg
                    width="19"
                    height="19"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-4 0v7h-4v-7a6 6 0 0 1 6-6z" />
                    <rect width="4" height="12" x="2" y="9" />
                    <circle cx="4" cy="4" r="2" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
          <div className="col-span-1 md:col-span-7">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="col-span-1">
                <h3 className="dm-serif-display-regular text-md">Product</h3>
                <ul className="mt-4 space-y-4 text-white/60 text-bodySmall">
                  <li>
                    <a href="#why" className="hover:underline">
                      Features
                    </a>
                  </li>
                  <li>
                    <a href="#pricing" className="hover:underline">
                      Pricing
                    </a>
                  </li>
                  <li>
                    <a href="#faq" className="hover:underline">
                      FAQ
                    </a>
                  </li>
                  <li>
                    <a href="/sign-up" className="hover:underline">
                      Create Account
                    </a>
                  </li>
                </ul>
              </div>

              <div className="col-span-1">
                <h3 className="dm-serif-display-regular text-md">Company</h3>
                <ul className="mt-4 space-y-4 text-white/60 text-bodySmall">
                  <li>
                    <a href="mailto:admin@legacykeeper.app" className="hover:underline">
                      Contact Us
                    </a>
                  </li>
                  <li>
                    <a href={`${baseUrl}/privacy-policy`} className="hover:underline">
                      Privacy Policy
                    </a>
                  </li>
                  <li>
                    <a href={`${baseUrl}/data-deletion-agreement`} className="hover:underline">
                      Data Deletion
                    </a>
                  </li>
                  <li>
                    <a href={`${baseUrl}/terms-of-service`} className="hover:underline">
                      Terms of Service
                    </a>
                  </li>
                </ul>
              </div>

              <div className="col-span-1">
                <h3 className="dm-serif-display-regular text-md">Resources</h3>
                <ul className="mt-4 space-y-4 text-white/60 text-bodySmall">
                  <li>
                    <a href="https://legacykeeper.app/blog/" className="hover:underline">
                      Blog
                    </a>
                  </li>
                  <li>
                    <a href="https://podcast.legacykeeper.app" className="hover:underline">
                      Podcast
                    </a>
                  </li>

                  <li>
                    <a href="/sign-in" className="hover:underline">
                      Login
                    </a>
                  </li>
                </ul>
                <div className="mt-6">
                  <h3 className="dm-serif-display-regular text-md">Compliance</h3>
                  <a
                    href="https://legacy-keeper-app.trust.site/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3 inline-block"
                  >
                    <img src={sprinto} alt="Sprinto" className="h-4" />
                  </a>
                </div>
              </div>
            </div>
          </div>
          <hr className="col-span-1 md:col-span-12 opacity-20 mt-16" />

          <div className="col-span-1 md:col-span-12 flex flex-col gap-3 md:flex-row md:justify-between">
            <span className="text-bodySmall text-white/60">
              © 2026 Legacy Keeper. All rights reserved.
            </span>
            <span className="text-bodySmall text-white/60">
              Estate planning for financial advisors - powered by Legacy Keeper
            </span>
          </div>
        </div>
      </footer>
    </main>
  )
}
